import React from 'react';

// Msg #804 — Admin/Mod control panel that lives BELOW the Social pane of the
// top-tabs bar.  Admin sees Cloak toggle, Private-Rooms toggle, Ban, Puppet.
// Mods see only Ban + Puppet.
// Msg #968 — Now draggable by header (position persists). Background tints to
// the current room theme color.
const POS_KEY = 'mandala_admin_panel_pos';

const AdminRow = ({ label, on, onClick, testid }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px' }}>
    <span style={{ flex: 1, fontSize: 12, color: 'var(--room-text, #1c3a1c)' }}>{label}</span>
    <button
      data-testid={testid}
      onClick={onClick}
      style={{
        background: on ? '#2d6b1e' : '#5a2a2a',
        color: '#f5e6c0',
        border: '1px solid #3a2b14',
        padding: '2px 10px',
        fontSize: 11, fontWeight: 'bold',
        letterSpacing: 1, textTransform: 'uppercase',
        cursor: 'pointer',
      }}
    >{on ? 'ON' : 'OFF'}</button>
  </div>
);

export default function AdminModPanel({
  role,                 // 'admin' | 'mod' | null
  cloaked,
  onToggleCloak,
  privateRoomsAllowed,
  onTogglePrivateRooms,
  puppetNpc,
  onOpenBan,
  onOpenPuppet,
  onOpenAnnounce,       // Msg #970 — Announcement tab
  onOpenDeleteUser,     // Msg #986 — Admin: delete a user account
  onOpenShadowWhisper,  // Msg #987 — Admin: red-themed shadow whisper MONITOR
  onOpenPrivateRoomMonitor, // Msg #986 — Admin: retained private-room monitor
  onOpenManageMods,     // Msg #987 — Admin: manage moderators
  onOpenAddStatPoints,  // Msg #988 — Admin: legacy quick grant stat points form
  onOpenEditUserStats,  // Msg #994 — Admin: view+edit user stats popup
  onOpenEditNpcAffection,  // Msg #994 — Admin: view+edit NPC affection popup
  onGrandfatherToday,   // Msg #988 — Admin: one-click grandfather today
  onOpenCreateNpc,      // Msg #990 — Admin: create custom NPC
  onOpenManageNpcs,     // Msg #990 — Admin: manage custom NPCs
  onOpenUsersList,      // Msg #991 — Admin: browse all users
  themeColor,           // Msg #968 — current room theme color (light bg tint)
  hideHeader = false,   // Msg #993 — when nested in the Social/Admin slot layout
                        // the outer slot already renders an "ADMIN" title, so
                        // we suppress the internal header here to avoid dupes.
}) {
  const [floating, setFloating] = React.useState(() => {
    try {
      const raw = localStorage.getItem(POS_KEY);
      return !!(raw && JSON.parse(raw).floating);
    } catch { return false; }
  });
  const [pos, setPos] = React.useState(() => {
    try {
      const raw = localStorage.getItem(POS_KEY);
      const p = raw ? JSON.parse(raw) : null;
      if (p && typeof p.x === 'number' && typeof p.y === 'number') return { x: p.x, y: p.y };
    } catch { /* ignore */ }
    return { x: 20, y: 120 };
  });

  React.useEffect(() => {
    try {
      localStorage.setItem(POS_KEY, JSON.stringify({ ...pos, floating }));
    } catch { /* ignore */ }
  }, [pos, floating]);

  if (role !== 'admin' && role !== 'mod') return null;
  const isAdmin = role === 'admin';

  const startDrag = (e) => {
    e.preventDefault();
    // First drag also promotes the panel to "floating" mode.
    setFloating(true);
    const startX = e.clientX;
    const startY = e.clientY;
    const startPos = { ...pos };
    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      setPos({
        x: Math.max(0, startPos.x + dx),
        y: Math.max(0, startPos.y + dy),
      });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const dockToTop = () => {
    setFloating(false);
    setPos({ x: 20, y: 120 });
  };

  const Row = AdminRow;

  // Session AD (Msg #972) — user request: the "ADMIN" bar is always GRAY,
  // regardless of the room theme. Body still picks up --room-panel so it
  // stays consistent with the rest of the UI.
  const ADMIN_HEADER_GRAY = '#6a6a6a';
  const ADMIN_HEADER_TEXT = '#f5f5f0';
  void themeColor;  // Prop preserved for API compat but intentionally unused.

  const containerStyle = floating
    ? {
        position: 'fixed', left: pos.x, top: pos.y,
        zIndex: 1100, width: 280,
        border: '1px solid var(--room-border, #5a4a1d)',
        background: 'var(--room-panel, #f5e6c0)',
        color: 'var(--room-text, #1c3a1c)',
        fontFamily: 'Georgia, "Times New Roman", serif',
        boxShadow: '0 6px 22px rgba(20,40,20,0.35)',
        userSelect: 'none',
        transition: 'background 400ms ease-in-out, color 400ms ease-in-out',
      }
    : {
        border: '1px solid var(--room-border, #5a4a1d)',
        background: 'var(--room-panel, #f5e6c0)',
        color: 'var(--room-text, #1c3a1c)',
        marginTop: 8,
        fontFamily: 'Georgia, "Times New Roman", serif',
        transition: 'background 400ms ease-in-out, color 400ms ease-in-out',
      };

  return (
    <div data-testid="admin-mod-panel" style={containerStyle}>
      {!hideHeader && (
        <div
          onMouseDown={startDrag}
          title="Drag to move — click ⤢ to dock"
          style={{
            background: ADMIN_HEADER_GRAY, color: ADMIN_HEADER_TEXT,
            padding: '3px 8px', fontSize: 11,
            letterSpacing: 2, textTransform: 'uppercase',
            textAlign: 'center', fontWeight: 'bold',
            cursor: 'move',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            textShadow: '0 1px 2px rgba(0,0,0,0.6)',
            borderBottom: '1px solid #4a4a4a',
          }}
          data-testid="admin-mod-header"
        >
          <span style={{ flex: 1 }}>{isAdmin ? 'ADMIN' : 'MODERATOR'}</span>
          {floating && (
            <span
              onClick={(e) => { e.stopPropagation(); dockToTop(); }}
              onMouseDown={(e) => e.stopPropagation()}
              style={{ cursor: 'pointer', padding: '0 6px', fontSize: 12 }}
              title="Dock back to sidebar"
              data-testid="admin-mod-dock"
            >⤢</span>
          )}
        </div>
      )}

      {isAdmin && (
        <Row label="Cloaked" on={!!cloaked} onClick={() => onToggleCloak(!cloaked)} testid="adminmod-toggle-cloak" />
      )}
      {isAdmin && (
        <Row label="Private rooms allowed" on={privateRoomsAllowed !== false} onClick={() => onTogglePrivateRooms(!privateRoomsAllowed)} testid="adminmod-toggle-privaterooms" />
      )}

      <div style={{ display: 'flex', gap: 6, padding: '4px 8px' }}>
        <button
          data-testid="adminmod-open-ban"
          onClick={onOpenBan}
          style={{
            flex: 1,
            background: '#8b1a1a', color: '#fff3d0',
            border: '1px solid #3a1a1a',
            padding: '4px 10px',
            fontSize: 11, fontWeight: 'bold',
            letterSpacing: 1, textTransform: 'uppercase',
            cursor: 'pointer',
          }}
        >Ban User</button>
        <button
          data-testid="adminmod-open-puppet"
          onClick={onOpenPuppet}
          style={{
            flex: 1,
            background: '#6b3a92', color: '#f5e6c0',
            border: '1px solid #3a1a5a',
            padding: '4px 10px',
            fontSize: 11, fontWeight: 'bold',
            letterSpacing: 1, textTransform: 'uppercase',
            cursor: 'pointer',
          }}
        >{puppetNpc ? `Puppet: ${puppetNpc}` : 'Puppet NPC'}</button>
      </div>

      {/* Msg #970 — Announcement tab (Goddess_Sophetia reads it aloud). */}
      <div style={{ padding: '2px 8px 8px' }}>
        <button
          data-testid="adminmod-open-announce"
          onClick={onOpenAnnounce}
          style={{
            width: '100%',
            background: '#c8a13a', color: '#3a2a1a',
            border: '1px solid #7a5a1a',
            padding: '5px 10px',
            fontSize: 11, fontWeight: 'bold',
            letterSpacing: 1, textTransform: 'uppercase',
            cursor: 'pointer',
          }}
        >📢 Announcement</button>
      </div>

      {/* Msg #986 — Admin-only buttons: Shadow Whisper, Private Rooms, Delete User, Manage Mods. */}
      {isAdmin && (
        <div style={{ padding: '0 8px 8px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {onOpenShadowWhisper && (
            <button
              data-testid="adminmod-open-shadow-whisper"
              onClick={onOpenShadowWhisper}
              title="Read-only red feed of every whisper between users"
              style={{
                width: '100%',
                background: '#7a0f14', color: '#ffe1dc',
                border: '1px solid #3a0708',
                padding: '5px 10px',
                fontSize: 11, fontWeight: 'bold',
                letterSpacing: 1, textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >🥷 Shadow Whisper Monitor</button>
          )}
          {onOpenPrivateRoomMonitor && (
            <button
              data-testid="adminmod-open-private-monitor"
              onClick={onOpenPrivateRoomMonitor}
              style={{
                width: '100%',
                background: '#1c3a3a', color: '#dff5f5',
                border: '1px solid #0a1f1f',
                padding: '5px 10px',
                fontSize: 11, fontWeight: 'bold',
                letterSpacing: 1, textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >🕵 Private Rooms</button>
          )}
          {onOpenManageMods && (
            <button
              data-testid="adminmod-open-manage-mods"
              onClick={onOpenManageMods}
              title="View and demote current moderators"
              style={{
                width: '100%',
                background: '#2d6b1e', color: '#ecfcd9',
                border: '1px solid #1c3a1c',
                padding: '5px 10px',
                fontSize: 11, fontWeight: 'bold',
                letterSpacing: 1, textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >👥 Manage Mods</button>
          )}
          {onOpenDeleteUser && (
            <button
              data-testid="adminmod-open-delete-user"
              onClick={onOpenDeleteUser}
              style={{
                width: '100%',
                background: '#3a0a0a', color: '#fce2d0',
                border: '1px solid #7a1a1a',
                padding: '5px 10px',
                fontSize: 11, fontWeight: 'bold',
                letterSpacing: 1, textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >🗑 Delete User</button>
          )}
          {/* Msg #988 — Admin: grant stat points to any user (kills + overrides). */}
          {onOpenAddStatPoints && (
            <button
              data-testid="adminmod-open-add-stat-points"
              onClick={onOpenAddStatPoints}
              title="Grant kills / override HP/MP/Atk/Def for a user"
              style={{
                width: '100%',
                background: '#4a3a1a', color: '#ffe1a0',
                border: '1px solid #a08a30',
                padding: '5px 10px',
                fontSize: 11, fontWeight: 'bold',
                letterSpacing: 1, textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >⚡ Stat Points</button>
          )}
          {/* Msg #994 — Admin: view + edit a user's live Character Stats. */}
          {onOpenEditUserStats && (
            <button
              data-testid="adminmod-open-edit-user-stats"
              onClick={onOpenEditUserStats}
              title="View and edit a user's HP / MP / Atk / Def / rank"
              style={{
                width: '100%',
                background: '#2d4a6b', color: '#dcefff',
                border: '1px solid #0f2a4a',
                padding: '5px 10px',
                fontSize: 11, fontWeight: 'bold',
                letterSpacing: 1, textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >📊 Character Stats</button>
          )}
          {/* Msg #994 — Admin: view + edit NPC affection / dislike toward a user. */}
          {onOpenEditNpcAffection && (
            <button
              data-testid="adminmod-open-edit-npc-affection"
              onClick={onOpenEditNpcAffection}
              title="Set how an NPC / Monster feels toward a specific user"
              style={{
                width: '100%',
                background: '#8a1a4a', color: '#ffe1ec',
                border: '1px solid #3a0a1a',
                padding: '5px 10px',
                fontSize: 11, fontWeight: 'bold',
                letterSpacing: 1, textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >♥ NPC Affection</button>
          )}
          {/* Msg #988 — One-click Grandfather today's rewards to all admins+mods. */}
          {onGrandfatherToday && (
            <button
              data-testid="adminmod-grandfather-today"
              onClick={onGrandfatherToday}
              title="Grant +1 kill and Priestess blessing to every admin+mod for today (once/day)"
              style={{
                width: '100%',
                background: '#1c5a1c', color: '#ecfcd9',
                border: '1px solid #0a3a0a',
                padding: '5px 10px',
                fontSize: 11, fontWeight: 'bold',
                letterSpacing: 1, textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >🎁 Grandfather Today</button>
          )}
          {/* Msg #990 — Admin: create + manage custom NPCs. */}
          {onOpenCreateNpc && (
            <button
              data-testid="adminmod-open-create-npc"
              onClick={onOpenCreateNpc}
              title="Create a custom NPC / Monster / Pokemon / Pokemorph"
              style={{
                width: '100%',
                background: '#3a4a1c', color: '#ecfcd9',
                border: '1px solid #7a9a2a',
                padding: '5px 10px',
                fontSize: 11, fontWeight: 'bold',
                letterSpacing: 1, textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >🐣 Create NPC</button>
          )}
          {onOpenManageNpcs && (
            <button
              data-testid="adminmod-open-manage-npcs"
              onClick={onOpenManageNpcs}
              title="Edit or delete custom NPCs"
              style={{
                width: '100%',
                background: '#4a3a4a', color: '#f0d9f0',
                border: '1px solid #7a5a7a',
                padding: '5px 10px',
                fontSize: 11, fontWeight: 'bold',
                letterSpacing: 1, textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >🐾 Manage NPCs</button>
          )}
          {/* Msg #991 — Admin: browse all users, jump to profile. */}
          {onOpenUsersList && (
            <button
              data-testid="adminmod-open-users-list"
              onClick={onOpenUsersList}
              title="Browse all users; click a name to view their profile"
              style={{
                width: '100%',
                background: '#2d4a6b', color: '#dcefff',
                border: '1px solid #0f2a4a',
                padding: '5px 10px',
                fontSize: 11, fontWeight: 'bold',
                letterSpacing: 1, textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >👥 Users</button>
          )}
        </div>
      )}
    </div>
  );
}
